import { Box, Typography } from '@mui/material';
import { useLoadBalancingConfig } from 'hooks/useLoadBalancingConfig';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import type { FC } from 'react';
import { time } from 'utils/common/convertNumbers';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';

import { initialState } from '..';
import type { AddLoadBalancingGroupModalStageProps } from '..';

const AddDevicesToGroupsConflict: FC<AddLoadBalancingGroupModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
  onClose,
}) => {
  const { t } = useTranslation();

  const { loadBalancingData, selectedDevices, selectedGroup } = state;

  const { onSave, loading, error } = useLoadBalancingConfig({
    onSuccess: result => {
      const pendingDevices = result.results.filter(
        device => device.pending || !device.success,
      );

      if (selectedGroup && pendingDevices.length === 0) {
        setState(initialState);
        onClose();

        return;
      }

      setState(prev => ({
        ...prev,
        conflictingSerials: [],
        stage: 'success',
        selectedDevices,
        pendingDevices,
      }));

      if (pendingDevices.length === 0) {
        setTimeout(() => {
          setState(initialState);
          onClose();
        }, time.seconds(5));
      }
    },
  });

  const handleMoveDevicesToLoadBalancingGroup = useCallback(() => {
    if (!loadBalancingData) return;

    onSave({
      serials: selectedDevices,
      log: loadBalancingData.loadBalancingGroupId,
      loe: loadBalancingData.loadBalancingEnabled,
      amp: loadBalancingData.maxTotalCurrent,
      sta: loadBalancingData.maxTotalCurrent,
      dyn: loadBalancingData.maxGridCurrent,
    });
  }, [onSave, selectedDevices, loadBalancingData]);

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        gap={2}
        mb={4}
      >
        <Typography variant="h2" mb={2}>
          {t(
            'common:pages.load_balancing.add_load_balancing_group.move_chargers_dialog.title',
          )}
        </Typography>
        <Typography variant="body1">
          {t(
            'common:pages.load_balancing.add_load_balancing_group.move_chargers_dialog.description',
          )}
        </Typography>
      </Box>

      <ErrorText error={error} />

      <Box display="flex" flexDirection="row" gap={2} mt={2} width="100%">
        <Button
          variant="flat"
          size="large"
          color="neutral"
          fullWidth
          disabled={loading}
          onClick={onCloseProxy}
          data-testid="go-back-button"
        >
          {t('common:go_back')}
        </Button>
        <Button
          variant="flat"
          size="large"
          color="primary"
          fullWidth
          disabled={loading}
          onClick={handleMoveDevicesToLoadBalancingGroup}
          data-testid="conflict-move-button"
        >
          {t(
            'common:pages.load_balancing.add_load_balancing_group.move_chargers_dialog.move_chargers',
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default AddDevicesToGroupsConflict;
